import { MdDriveFolderUpload, MdMovieFilter } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Uploadvideo } from "../../../../redux/ApiSlice/UploadVideoSlice";
import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const RecordingControls = ({
    isRecording,
    isPaused,
    startRecording,
    pauseRecording,
    handleDraftClick,
    selectedTimer,
    onTimerSelect,
    timerOptions,
    formatTimerForDisplay,
    elapsedTime,
    formatTime,
    getProgress,
    getProgressStyle,
    pauseMarkers = [],
    setShowFormatAlert,
    onOpenFilterSheet,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [isTimerExpanded, setIsTimerExpanded] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (timerRef.current && !timerRef.current.contains(e.target)) {
                setIsTimerExpanded(false);
            }
        };
        if (isTimerExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isTimerExpanded]);
    // Convert timer string to seconds
    const convertTimerToSeconds = (timerString) => {
        const [minutes, seconds] = timerString.split(':').map(Number);
        return minutes * 60 + seconds;
    };

    // Handle video file selection
    const handleVideoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size <= 0) {
                setShowFormatAlert(true);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }
            console.log("Selected file:", file);
            const fileURL = URL.createObjectURL(file);


            // Dispatch video to Redux store
            dispatch(Uploadvideo({
                fileURL: fileURL,
                liveStream: false
            })).then(() => {
                // Navigate to upload page after dispatch
                navigate("/uploadvideo");
            });
        }
    };

    // Trigger file input click
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Calculate angle for each pause marker
    const getPauseMarkerStyle = (pauseTime) => {
        const totalSeconds = convertTimerToSeconds(selectedTimer);
        const progress = pauseTime / totalSeconds;
        const degrees = progress * 360;
        return {
            transform: `rotate(${degrees}deg)`,
            transformOrigin: 'center center'
        };
    };

    // Handle timer button click
    const handleTimerClick = () => {
        setIsTimerExpanded(!isTimerExpanded);
    };

    // Handle timer option selection
    const handleTimerOptionSelect = (value) => {
        onTimerSelect(value);
        setIsTimerExpanded(false);
    };
    return (
        <div className="w-full absolute bottom-0 mb-4">
            <div className="relative w-full flex items-end justify-center">
                <div
                    onClick={isRecording ? pauseRecording : startRecording}
                    className="h-12 w-12 rounded-full border-[3px] border-white flex items-center justify-center cursor-pointer relative overflow-hidden"
                    style={(isRecording || isPaused) ? getProgressStyle() : { background: 'transparent' }}
                >
                    {/* Pause marker lines - display all pause markers */}
                    {pauseMarkers.map((pauseTime, index) => (
                        <div
                            key={index}
                            className="absolute w-full h-full"
                            style={getPauseMarkerStyle(pauseTime)}
                        >
                            <div
                                className="absolute w-[1px] bg-gray-900"
                                style={{
                                    height: '24px', // From center (24px / 2 = 12px from center, leaving 4px for inner circle radius 8px = 16px line)
                                    left: '50%',
                                    top: '0px', // Start from 8px (center circle radius)
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        </div>
                    ))}
                    <div className={`h-4 w-4 rounded-full ${(isRecording || isPaused) ? 'bg-white' : 'bg-red-500'} relative z-10`} />
                </div>

                <div className="absolute -top-2 left-3 flex flex-col gap-y-2">
                    <div className="relative" ref={timerRef}>
                        <button
                            onClick={handleTimerClick}
                            className="w-20 text-[14px] rounded font-bold bg-[#BDE6FF] text-black py-[3px]"
                        >
                            {selectedTimer === "00:15" ? "Timer" : formatTimerForDisplay(selectedTimer)}
                        </button>

                        <AnimatePresence>
                            {isTimerExpanded && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute left-0 bottom-full mb-2 bg-white rounded-lg shadow-lg p-2 w-32 z-50"
                                >
                                    <div className="grid grid-cols-2 gap-2">
                                        {timerOptions.map((option, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleTimerOptionSelect(option.value)}
                                                className={`py-1 px-2 text-[12px] font-medium rounded transition-colors ${selectedTimer === option.value
                                                    ? 'bg-[#BDE6FF] text-black'
                                                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <button
                        onClick={handleDraftClick}
                        className="w-20 text-[14px] rounded font-bold bg-[#BDE6FF] text-black py-[3px]"
                    >
                        Drafts
                    </button>
                </div>

                {/* <button
                    onClick={handleTimerClick}
                    className="absolute top-3 right-10 px-2 py-[1px] text-white bg-black/50 rounded cursor-pointer"
                >
                    {(isRecording || isPaused) ? formatTime(elapsedTime) : selectedTimer}
                </button> */}
                <div className="absolute top-3 right-6 flex items-center gap-x-3">
                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="video/*"
                        className="hidden"
                        onChange={handleVideoSelect}
                    />
                    <button
                        onClick={handleUploadClick}
                        className="rounded bg-[#BDE6FF] w-9 h-9 flex items-center justify-center">
                        <MdDriveFolderUpload className="text-black text-[22px] cursor-pointer" />
                    </button>
                    <button
                        onClick={onOpenFilterSheet}
                        className="rounded bg-[#BDE6FF] w-9 h-9 flex items-center justify-center">
                        <MdMovieFilter className="text-black text-[22px] cursor-pointer" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecordingControls;